/**
 * Defines a set of retry policies and provides a utility function to perform
 * asynchronous retries with various retry strategies.
 */
import {Errors, KAError} from "@khan/logging";

export const CancellationError = new KAError(
    "retry was cancelled",
    Errors.Internal,
);
export const MaxRetriesError = new KAError(
    "exceeded retry attempts",
    Errors.Internal,
);

/**
 * An enumeration of the supported retry strategies.
 */
export const RetryPolicies = {
    ExponentialBackoff: "exponential-backoff", // Exponential delay between retries
    Fixed: "fixed", // Fixed delay between retries
};

type RetryPolicy = (typeof RetryPolicies)[keyof typeof RetryPolicies];

/**
 * Represents a retry operation function that can be retried with a delay.
 */
interface RetryOperation<T> {
    /**
     * The retry operation to execute with a retry strategy.
     *
     * @param retry - The retry function to invoke within the retry operation.
     * @param retryCount - The number of retries already attempted.
     * @param delayMs - The delay (in milliseconds) before the retry.
     * @returns {Promise<T>} Resolves with the result of the operation.
     */
    (retry: RetryFn<T>, retryCount: number, delayMs: number): Promise<T>;
}

/**
 * A function that can be retried and provides control to cancel the retry.
 */
type RetryFn<T> = () => {
    result: Promise<T>;
    cancel: () => void;
};

/**
 * Configuration options for retry operations.
 */
interface Config {
    initialDelayMs?: number; // Initial delay before the first retry attempt
    maxDelayMs?: number; // Maximum delay between retry attempts
    maxRetries?: number; // Maximum number of retry attempts
    policy?: RetryPolicy; // Retry policy ("fixed" by default)
}

const defaultConfig = {
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    maxRetries: 10,
    policy: RetryPolicies.Fixed,
};

/**
 * Performs asynchronous retries using the given retry strategy.
 *
 * @param fn - The retry operation function to execute under a retry strategy.
 * @param config - Configuration options for retry operations.
 * @returns {Promise<T>} A promise that resolves with the result of the retry operation.
 *
 * @throws {CancellationError} When the retry operation is explicitly cancelled using the `cancelRetry` function.
 * @throws {MaxRetriesError} When the maximum number of retry attempts specified in the `maxRetries` configuration is reached.
 */
export const retryWithDelay = <T>(
    fn: RetryOperation<T>,
    config?: Config,
): Promise<T> =>
    new Promise((resolve, reject) => {
        const {initialDelayMs, maxDelayMs, maxRetries, policy} = {
            ...defaultConfig,
            ...config,
        };
        let retryNum = 0;

        const retryFn: RetryFn<T> = () => {
            // The retryPromise is responsible for setting a timeout with the
            // appropriate delay if the maximum retries hasn't been reached; other-
            // wise we throw an error.
            let timeoutId: number | undefined;
            const retryPromise: Promise<T> = new Promise(
                (retryResolve, retryReject) => {
                    retryNum++;

                    const delayMs = getDelayMs(
                        initialDelayMs,
                        maxDelayMs,
                        retryNum,
                        policy,
                    );

                    // We throw an error if the max number of retries has been reached.
                    if (retryNum > maxRetries) {
                        return retryReject(MaxRetriesError);
                    }
                    // We compute the next delay time so that callers can use that
                    // data (i.e. for analytics, UI) before calling the next retry.
                    const nextDelayMs = getDelayMs(
                        initialDelayMs,
                        maxDelayMs,
                        retryNum + 1,
                        policy,
                    );

                    // eslint-disable-next-line no-restricted-syntax -- porting over from old code
                    timeoutId = window.setTimeout(
                        () =>
                            fn(retryFn, retryNum, nextDelayMs)
                                .then(retryResolve)
                                .catch(retryReject),
                        delayMs,
                    );
                },
            );

            // We create a promise that can be used to cancel the current retry
            // if `cancelRetry` is invoked.
            let cancelRetry = () => {};
            const cancelPromise: Promise<T> = new Promise((_, cancelReject) => {
                cancelRetry = () => {
                    // eslint-disable-next-line no-restricted-syntax -- porting over from old code
                    window.clearTimeout(timeoutId);
                    cancelReject(CancellationError);
                };
            });

            // Use a Promise.race to enable the cancel promise to abort the
            // retry timeout.
            return {
                result: Promise.race([retryPromise, cancelPromise])
                    .then(resolve)
                    .catch(reject) as Promise<T>,
                cancel: cancelRetry,
            };
        };

        fn(
            retryFn,
            retryNum,
            getDelayMs(initialDelayMs, maxDelayMs, retryNum + 1, policy),
        )
            .then(resolve)
            .catch(reject);
    });

/**
 * Calculates the delay (in milliseconds) for the next retry attempt based on
 * the current retry count and the chosen retry policy.
 *
 * @param initialDelayMs - The initial delay in milliseconds.
 * @param maxDelayMs - The maximum allowed delay in milliseconds.
 * @param retryNum - The current retry count.
 * @param policy - The selected retry policy ("fixed" or "exponential-backoff").
 * @returns The delay in milliseconds for the next retry attempt.
 */
export const getDelayMs = (
    initialDelayMs: number,
    maxDelayMs: number,
    retryNum: number,
    policy: RetryPolicy,
): number => {
    // Retry = 0 represents the initial function call. We do not want to apply
    // a delay on the first call, so we'll return 0.
    if (retryNum === 0) {
        return 0;
    }

    switch (policy) {
        case RetryPolicies.ExponentialBackoff:
            return Math.min(
                initialDelayMs * Math.pow(2, retryNum - 1),
                maxDelayMs,
            );
        case RetryPolicies.Fixed:
        default:
            return initialDelayMs;
    }
};
